import React, { useEffect, useState } from 'react';
import api from '../api';

const THEME = {
    card: '#1e293b', border: '#334155', textSec: '#94a3b8', 
    primary: '#3b82f6', success: '#10b981', danger: '#ef4444', warning: '#f59e0b',
    bg: '#0f172a'
};

const Analytics = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const res = await api.get('analytics/');
                setStats(res.data);
                setLoading(false);
            } catch (err) {
                console.error("Error loading analytics", err);
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    if (loading || !stats) return <div style={{color:'white', padding:'20px'}}>Loading Analytics...</div>;

    // --- SUB-COMPONENTS ---
    const StatCard = ({ title, value, icon, color, subtext }) => (
        <div style={{
            background: THEME.card, 
            padding: '20px', 
            borderRadius: '12px', 
            border: `1px solid ${THEME.border}`, 
            borderLeft: `4px solid ${color}`,
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'space-between',
            height: '100%',
            boxSizing: 'border-box'
        }}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'start'}}>
                <div>
                    <div style={{color: THEME.textSec, fontSize: '0.85rem', fontWeight:'600', letterSpacing:'0.5px', textTransform:'uppercase', marginBottom:'5px'}}>{title}</div>
                    <div style={{fontSize: '2rem', fontWeight: 'bold', color: 'white', lineHeight: '1'}}>
                        {value}
                    </div>
                </div>
                <div style={{fontSize:'1.5rem', opacity:0.8}}>{icon}</div>
            </div>
            {subtext && <div style={{color: THEME.textSec, fontSize: '0.8rem', marginTop:'10px', paddingTop:'10px', borderTop:`1px dashed ${THEME.border}`}}>{subtext}</div>}
        </div>
    );

    const BarChart = ({ data, color }) => (
        <div style={{display:'flex', flexDirection:'column', gap:'12px'}}>
            {data && data.map((item, index) => (
                <div key={index}>
                    <div style={{display:'flex', justifyContent:'space-between', fontSize:'0.85rem', color: THEME.textSec, marginBottom:'4px'}}>
                        <span>{item.name}</span>
                        <span style={{color:'white', fontWeight:'bold'}}>{item.count}</span>
                    </div>
                    <div style={{background: '#0f172a', height:'8px', width:'100%', borderRadius:'4px', overflow:'hidden', border: `1px solid ${THEME.border}`}}>
                        <div style={{
                            width: `${Math.min((item.count / 10) * 100, 100)}%`, 
                            background: color,
                            height:'100%',
                            borderRadius:'4px',
                            transition: 'width 0.5s ease-in-out'
                        }}></div>
                    </div>
                </div>
            ))}
            {(!data || data.length === 0) && <div style={{color: THEME.textSec, fontStyle:'italic', fontSize:'0.8rem', padding:'10px', textAlign:'center', background:'#0f172a', borderRadius:'6px'}}>No data recorded yet.</div>}
        </div>
    );

    return (
        <div className="animate-fade-in">
            {/* HEADER */}
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'30px', borderBottom:`1px solid ${THEME.border}`, paddingBottom:'20px'}}>
                <h2 style={{color: 'white', margin:0}}>📊 Site Analytics</h2>
                <div style={{fontSize:'0.9rem', color: THEME.textSec}}>
                    Last Updated: {new Date().toLocaleTimeString()}
                </div>
            </div>

            {/* ROW 1: LIVE STATS (TODAY ONLY) */}
            <div style={{marginBottom:'40px'}}>
                <h4 style={{color: THEME.primary, margin:'0 0 15px', display:'flex', alignItems:'center', gap:'10px'}}>
                    <span style={{height:'10px', width:'10px', background: THEME.danger, borderRadius:'50%', boxShadow:`0 0 10px ${THEME.danger}`}}></span>
                    LIVE SITE STATUS (TODAY)
                </h4>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px'}}>
                    <StatCard title="Active Headcount" value={stats.live.headcount} icon="👷" color={THEME.primary} subtext="People currently checked in" />
                    <StatCard title="Contractors" value={stats.live.companies} icon="🏢" color="#8b5cf6" subtext="Companies active on site" />
                    <StatCard title="Man-Hours Today" value={stats.live.today_hours} icon="⏱️" color={THEME.success} subtext="Hours accumulated so far" />
                </div>
            </div>

            {/* ROW 2: PROJECT TOTALS & SAFETY */}
            <h4 style={{color: THEME.textSec, margin:'0 0 15px', textTransform:'uppercase', letterSpacing:'1px', fontSize:'0.8rem'}}>Project Cumulative</h4>
            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '40px', alignItems: 'stretch'}}>
                
                {/* 1. TOTAL PROJECT HOURS */}
                <StatCard 
                    title="Total Project Hours" 
                    value={stats.project_totals.total_man_hours} 
                    icon="🏗️" 
                    color={THEME.success} 
                    subtext="Total cumulative hours since project start"
                />

                {/* 2. SAFETY INCIDENTS CARD */}
                <div style={{background: THEME.card, padding:'20px', borderRadius:'12px', border:`1px solid ${THEME.border}`}}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px', borderBottom:`1px dashed ${THEME.border}`, paddingBottom:'10px'}}>
                        <h4 style={{margin:0, color:'white', fontSize:'1rem'}}>🦺 Safety Incidents</h4>
                        <span style={{fontSize:'0.75rem', color: THEME.textSec}}>ALL TIME</span>
                    </div>
                    
                    <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px', textAlign:'center'}}>
                        <div style={{background:'#0f172a', padding:'10px', borderRadius:'8px', border:`1px solid ${THEME.danger}44`}}>
                            <div style={{fontSize:'1.5rem', fontWeight:'bold', color: THEME.danger}}>{stats.safety_incidents.incident}</div>
                            <div style={{color: THEME.textSec, fontSize:'0.7rem', fontWeight:'bold'}}>INCIDENTS</div>
                        </div>
                        <div style={{background:'#0f172a', padding:'10px', borderRadius:'8px', border:`1px solid ${THEME.warning}44`}}>
                            <div style={{fontSize:'1.5rem', fontWeight:'bold', color: THEME.warning}}>{stats.safety_incidents.near_miss}</div>
                            <div style={{color: THEME.textSec, fontSize:'0.7rem', fontWeight:'bold'}}>NEAR MISS</div>
                        </div>
                        <div style={{background:'#0f172a', padding:'10px', borderRadius:'8px', border:`1px solid ${THEME.primary}44`}}>
                            <div style={{fontSize:'1.5rem', fontWeight:'bold', color: THEME.primary}}>{stats.safety_incidents.hazard}</div>
                            <div style={{color: THEME.textSec, fontSize:'0.7rem', fontWeight:'bold'}}>HAZARDS</div>
                        </div>
                    </div>
                </div>

                {/* 3. PERMITS CARD */}
                <div style={{background: THEME.card, padding:'20px', borderRadius:'12px', border:`1px solid ${THEME.border}`}}>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px', borderBottom:`1px dashed ${THEME.border}`, paddingBottom:'10px'}}>
                        <h4 style={{margin:0, color:'white', fontSize:'1rem'}}>📜 Work Permits</h4>
                        <span style={{fontSize:'0.75rem', color: THEME.textSec}}>ALL TIME</span>
                    </div>

                    <div style={{display:'flex', justifyContent:'space-around', alignItems:'center', height:'80px'}}>
                        <div style={{textAlign:'center'}}>
                            <div style={{fontSize:'1.5rem', fontWeight:'bold', color: THEME.warning}}>{stats.permits.pending}</div>
                            <div style={{color: THEME.textSec, fontSize:'0.7rem'}}>Pending</div>
                        </div>
                        <div style={{width:'1px', height:'40px', background: THEME.border}}></div>
                        <div style={{textAlign:'center'}}>
                            <div style={{fontSize:'1.5rem', fontWeight:'bold', color: THEME.success}}>{stats.permits.approved}</div>
                            <div style={{color: THEME.textSec, fontSize:'0.7rem'}}>Approved</div>
                        </div>
                        <div style={{width:'1px', height:'40px', background: THEME.border}}></div>
                        <div style={{textAlign:'center'}}>
                            <div style={{fontSize:'1.5rem', fontWeight:'bold', color: THEME.danger}}>{stats.permits.rejected}</div>
                            <div style={{color: THEME.textSec, fontSize:'0.7rem'}}>Rejected</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ROW 3: AUDIT FINDINGS */}
            <div style={{background: THEME.card, padding:'25px', borderRadius:'12px', border:`1px solid ${THEME.border}`}}>
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'25px'}}>
                    <h3 style={{margin:0, color:'white'}}>⚠️ Audit Insights</h3>
                    <span style={{background:'#0f172a', color: THEME.textSec, fontSize:'0.75rem', padding:'4px 10px', borderRadius:'20px', border:`1px solid ${THEME.border}`}}>Excluding 'Good Practice'</span>
                </div>
                
                <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '50px'}}>
                    <div>
                        <h4 style={{color: THEME.danger, fontSize:'0.85rem', marginBottom:'20px', textTransform:'uppercase', borderLeft:`3px solid ${THEME.danger}`, paddingLeft:'10px'}}>Top 5 Violation Types</h4>
                        <BarChart data={stats.audit_insights.top_violations} color={THEME.danger} />
                    </div>
                    <div>
                        <h4 style={{color: THEME.warning, fontSize:'0.85rem', marginBottom:'20px', textTransform:'uppercase', borderLeft:`3px solid ${THEME.warning}`, paddingLeft:'10px'}}>Violations by Contractor</h4>
                        <BarChart data={stats.audit_insights.by_company} color={THEME.warning} />
                    </div>
                </div>
            </div>

        </div>
    );
};

export default Analytics;